<template>
  <div class="my-3">
    <p
      v-if="title"
      class="body-2 font-weight-bold"
    >
      {{ title }}
    </p>
    <div class="app-align-items-center">
      <v-icon
        color="gray74"
        dark
        small
        class="mr-1"
        :style="{ 'opacity': showConfigs ? '0.3' : '1' }"
      >
        mdi-information
      </v-icon>
      <span
        class="text-truncate"
        style="white-space: nowrap;"
        :style="{ 'opacity': showConfigs ? '0.3' : '0.7' }"
      >F12/ESC no editor para fullscreen</span>
      <v-spacer />
      <BaseButton
        :has-icon="true"
        button-class="my-0 ml-4 mr-1 pa-4"
        size="x-small"
        @click="showConfigs = !showConfigs"
      >
        <v-icon>{{ !showConfigs ? 'settings' : 'close' }}</v-icon>
      </BaseButton>
      <v-select
        v-if="showConfigs"
        v-model="modeSelected"
        :items="modeList"
        class="mr-2"
        label="Linguagem"
        dense
        hide-details
      />
      <v-select
        v-if="showConfigs"
        v-model="themeSelected"
        :items="themeList"
        class="mr-2"
        label="Tema"
        dense
        hide-details
        @change="changeTheme(themeSelected)"
      />
    </div>
    <div :class="{ 'is-homolog': isHomolog }">
      <codemirror
        :ref="cmRef"
        :options="{ ...cmOptions, lint: useLint, readOnly: readOnly, mode: parseMode(modeSelected), theme: themeSelected }"
        class="my-2"
        :style="{ 'cursor' : readOnly ? 'not-allowed' : 'unset', '--app-cm-height': `${cmHeight}px`}"
        v-bind="{ ...$attrs, ...$props }"
        v-on="$listeners"
        @input="changeValue"
      />
    </div>
  </div>
</template>
<script>
import CodeMirror from 'codemirror'
import { codemirror } from 'vue-codemirror'
import jsYaml from 'js-yaml'
import UtilsLocalStorage from '../mixins/UtilsLocalStorage'
import UtilsCodemirror from '../mixins/UtilsCodemirror'
import jsonlint from '../assets/js/jsonlint.js'
import 'codemirror/addon/selection/active-line.js'
import 'codemirror/addon/edit/closebrackets.js'
import 'codemirror/addon/display/fullscreen.js'
import 'codemirror/addon/lint/json-lint.js'
import 'codemirror/addon/lint/lint.js'
import 'codemirror/addon/lint/lint.css'
import 'codemirror/lib/codemirror.css'
import '../assets/css/codemirror-lsp.css'
import 'codemirror/theme/base16-dark.css'
import 'codemirror/theme/base16-light.css'
import 'codemirror/theme/darcula.css'
import 'codemirror/theme/eclipse.css'
import 'codemirror/theme/yonce.css'
import 'codemirror/mode/groovy/groovy'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/mode/xml/xml'
import 'codemirror/mode/css/css'
import 'codemirror/mode/python/python'
import 'codemirror/mode/yaml/yaml'
import 'codemirror/mode/htmlmixed/htmlmixed'
import 'codemirror/mode/clike/clike'

CodeMirror.registerHelper('lint', 'yaml', function(text) {
  const found = []
  try {
    jsYaml.safeLoad(text)
  } catch (error) {
    const line = error.mark ? error.mark.line : 0
    const ch = error.mark ? error.mark.column : 0
    found.push({
      from: CodeMirror.Pos(line, ch),
      to: CodeMirror.Pos(line, ch + 1),
      message: error.message,
      severity: 'error'
    })
  }
  return found
})

export default {
  name: "BaseCodemirror",
  components: { codemirror },
  mixins: [ UtilsCodemirror, UtilsLocalStorage ],
  props: {
    cmRef: {
      type: String,
      required: false,
      default: 'cm-ref'
    },
    cmHeight: {
      type: Number,
      required: false,
      default: 250
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    lang: {
      type: String,
      required: false,
      default: 'groovy',
    }
  },
  data () {
    return {
      showConfigs: false,
      useLint: true,
      modeList: ['groovy', 'html', 'json', 'java', 'python', 'xml', 'yaml'],
      themeList: ['base16-dark', 'base16-light', 'darcula', 'eclipse', 'yonce'],
      modeSelected: this.lang,
      themeSelected: 'base16-dark',
      cmOptions: {
        tabSize: 4,
        lineNumbers: true,
        line: true,
        gutters: ["CodeMirror-lint-markers"],
        lineWrapping: true,
        styleActiveLine: true,
        autoCloseBrackets: true,
        extraKeys: {
          F12(cm) {
            const isFull = cm.getOption('fullScreen')
            cm.setOption('fullScreen', !isFull)
            document.body.classList.toggle('cm-fullscreen', !isFull)
          },
          Esc(cm) {
            if (cm.getOption('fullScreen')) {
              cm.setOption('fullScreen', false)
              document.body.classList.remove('cm-fullscreen')
            }
          }
        }
      },
      mixedMode: {
        name: "htmlmixed",
        scriptTypes: [
          { matches: /\/x-handlebars-template|\/x-mustache/i, mode: null },
          { matches: /(text|application)\/(x-)?vb(a|script)/i, mode: "vbscript"}
        ]
      }
    }
  },
  watch: {
    lang(){
      this.modeSelected = this.lang
    }
  },
  mounted() {
    window.jsonlint = jsonlint;

    let themeLocalstorage = this.getItemLocalStorage("idt-codemirror-theme")
    if(themeLocalstorage){
      this.themeSelected = themeLocalstorage
    }
    this.modeSelected = this.lang
    if(this.cmRef && !this.readOnly){
      setTimeout(() => {
        this.startLsp(this.cmRef, this.modeSelected)
      }, 400);
    }
  },
  beforeDestroy() {
    this.closeLsp()
  },
  methods: {
    parseMode(lang) {
      let mode = {
       'groovy': 'text/x-groovy',
       'json': 'application/json',
       'java': 'text/x-java',
       'yaml': 'text/x-yaml',
       'python': 'text/x-python',
       'html': this.mixedMode,
       'xml': 'application/xml'
      }
      return mode[lang]
    },
    changeTheme(theme){
      this.setItemLocalStorage("idt-codemirror-theme", theme)
    },
    changeValue(value){
      this.useLint = value.trim().length > 0
    }
  }
}
</script>
<style lang="scss">
.CodeMirror {
  height: var(--app-cm-height)!important;
  margin-top: 0;
  border: 1px solid #eee;
}
.CodeMirror-fullscreen {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 4000;
}
.is-homolog .CodeMirror-fullscreen {
  margin-top: 20px;
}
.CodeMirror, .CodeMirror-scroll {
  width: 100% !important;
  min-width: 0 !important;
  overflow-x: auto;
}
.CodeMirror-vscrollbar, .CodeMirror-hscrollbar {
  z-index: 2;
}
</style>
